// PROMPT 147: arma lo que ve el cliente al mandarle una plantilla, igual que el servidor
// (`plantillas.armar_envio_detallado`): lo que se ve en la vista previa es lo que queda en el hilo.
// Si cambia una regla aca, cambia tambien alla (hay una prueba que compara los dos textos).

const RE_PARAM_SUCIO = /[\r\n\t]+| {4,}/g;

// El valor de una variable como lo acepta Meta: sin saltos de linea, tabulaciones ni 4 espacios seguidos.
export function limpiarValor(valor) {
  return String(valor === null || valor === undefined ? "" : valor).replace(RE_PARAM_SUCIO, " ").trim();
}

// True si las variables tienen nombre ({{nombre_cliente}}) y no numero ({{1}}).
export function esNombrada(plantilla) {
  if (String(plantilla?.parameter_format || "").toUpperCase() === "NAMED") return true;
  return (plantilla?.componentes || []).some(
    (c) => c && /\{\{\s*[A-Za-z_][A-Za-z0-9_]*\s*\}\}/.test(String(c.text || ""))
  );
}

// Las variables de un texto en el orden en que hay que mandarlas: 1, 2, 3 o los nombres.
export function clavesDe(texto, nombrada) {
  const t = String(texto || "");
  if (!nombrada) {
    const numeros = Array.from(t.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1]));
    return Array.from(new Set(numeros)).sort((a, b) => a - b);
  }
  const vistas = [];
  Array.from(t.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g)).forEach((m) => {
    if (!vistas.includes(m[1])) vistas.push(m[1]);
  });
  return vistas;
}

function rellenar(texto, pares, conMarcas) {
  const mapa = new Map(pares.map(([k, v]) => [String(k), v]));
  return String(texto || "").replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (todo, k) => {
    if (!mapa.has(k)) return todo;
    const v = mapa.get(k);
    return conMarcas && !v ? todo : v;
  });
}

// [(clave, valor limpio)] de un grupo de valores (lista en orden o objeto por numero/nombre).
function resolver(grupo, claves, donde, faltan) {
  return claves.map((clave, i) => {
    let valor = null;
    if (Array.isArray(grupo)) valor = i < grupo.length ? grupo[i] : null;
    else if (grupo && typeof grupo === "object") valor = grupo[clave] ?? grupo[String(clave)];
    const limpio = limpiarValor(valor);
    if (!limpio) faltan.push(`Falta completar la variable {{${clave}}} del ${donde}.`);
    return [clave, limpio];
  });
}

const ENCABEZADOS_ARCHIVO = ["IMAGE", "VIDEO", "DOCUMENT"];

// `valores`: {encabezado: [..], cuerpo: [..] (en orden) o {nombre: valor}, botones: {"<indice>": "<sufijo>"}}.
// Devuelve {partes, texto, faltan}; `partes` dibuja la burbuja y `texto` es lo que guarda el servidor.
// Con `conMarcas`, lo que todavia no se completo queda como {{1}} para que se vea donde va.
export function vistaDePlantilla(plantilla, valores, { conMarcas = false } = {}) {
  const p = plantilla || {};
  const v = valores || {};
  const faltan = [];
  const nombrada = esNombrada(p);
  const partes = { encabezado: null, encabezado_tipo: null, cuerpo: "", pie: null, botones: [] };
  (p.componentes || []).forEach((c) => {
    if (!c || typeof c !== "object") return;
    const tipo = String(c.type || "").toUpperCase();
    const texto = String(c.text || "");
    if (tipo === "HEADER") {
      const formato = String(c.format || "").toUpperCase();
      partes.encabezado_tipo = formato;
      if (formato === "TEXT") {
        const claves = clavesDe(texto, nombrada);
        const pares = claves.length ? resolver(v.encabezado, claves, "encabezado", faltan) : [];
        partes.encabezado = rellenar(texto, pares, conMarcas);
      }
    } else if (tipo === "BODY") {
      const claves = clavesDe(texto, nombrada);
      const pares = claves.length ? resolver(v.cuerpo, claves, "cuerpo", faltan) : [];
      partes.cuerpo = rellenar(texto, pares, conMarcas);
    } else if (tipo === "FOOTER") {
      partes.pie = texto;
    } else if (tipo === "BUTTONS") {
      const sufijos = v.botones && typeof v.botones === "object" ? v.botones : {};
      (c.buttons || []).forEach((b, i) => {
        if (!b || typeof b !== "object") return;
        const rotulo = String(b.text || "");
        if (String(b.type || "").toUpperCase() === "URL" && String(b.url || "").includes("{{")) {
          if (!limpiarValor(sufijos[String(i)] ?? sufijos[i])) faltan.push(`Falta completar el link del botón «${rotulo}».`);
        }
        partes.botones.push(rotulo);
      });
    }
  });
  const bloques = [partes.encabezado, partes.cuerpo, partes.pie].filter(Boolean);
  let texto = bloques.join("\n\n");
  const lineas = partes.botones.filter(Boolean).map((x) => `[${x}]`).join("\n");
  if (lineas) texto = texto ? `${texto}\n\n${lineas}` : lineas;
  return { partes, texto, faltan };
}

// El formato de archivo que lleva el encabezado de la plantilla (IMAGE, VIDEO, DOCUMENT) o null.
export function encabezadoDeArchivo(plantilla) {
  const h = (plantilla?.componentes || []).find((c) => c && String(c.type || "").toUpperCase() === "HEADER");
  const formato = String(h?.format || "").toUpperCase();
  return ENCABEZADOS_ARCHIVO.includes(formato) ? formato : null;
}

// Los campos que hay que completar (`plantilla.campos`, del servidor) -> los `valores` que se mandan.
export function valoresDeCampos(plantilla, campos, vals) {
  const nombrada = esNombrada(plantilla);
  const salida = { encabezado: nombrada ? {} : [], cuerpo: nombrada ? {} : [], botones: {} };
  (campos || []).forEach((c) => {
    const valor = vals[`${c.grupo}:${c.clave}`] ?? "";
    if (c.grupo === "boton") salida.botones[String(c.clave)] = valor;
    else if (nombrada) salida[c.grupo][c.clave] = valor;
    else salida[c.grupo].push(valor);
  });
  return salida;
}

// Lo que se propone para cada variable a partir de la reserva o del cliente (`valores_sugeridos`).
export function valoresIniciales(campos, sugeridos) {
  const vals = {};
  (campos || []).forEach((c) => {
    const propuesto = c.autocompletar ? (sugeridos || {})[c.autocompletar] : "";
    vals[`${c.grupo}:${c.clave}`] = propuesto ? String(propuesto) : "";
  });
  return vals;
}

// El primer renglon de un texto (como el "ultimo mensaje" de la lista).
export function primerRenglon(texto) {
  return String(texto || "").split("\n").find((x) => x.trim()) || "";
}
